import Config from '../data/Config'
import { apiClient } from '../infra/http/apiClient'

/**
 * ChatService - Handles WebSocket connections and REST API calls for chat functionality
 */
class ChatService {
  constructor() {
    this.ws = null
    this.token = null
    this.reconnectAttempts = 0
    this.maxReconnectAttempts = 5
    this.reconnectDelay = 1000 // Start with 1 second
    this.messageHandler = null
    this.connectionStateHandler = null
    this.readyHandler = null
    this.isConnecting = false
    this.isAuthenticated = false
    this.pingTimeoutId = null
    this.pingTimeoutMs = 5000
  }

  /**
   * Get or create a 1v1 chat room
   * @param {string} instructorId - Instructor ID
   * @returns {Promise<{roomId: string}>} Room information
   */
  async getOrCreateRoom(instructorId) {
    try {
      const response = await apiClient.post('/chat/rooms', {
        users: [instructorId],
      })
      return response.data
    } catch (error) {
      console.error('Failed to get or create room:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get chat messages for a room
   * @param {string} roomId - Room ID
   * @param {number} limit - Number of messages to fetch (default: 50, clamped to 1-200)
   * @param {number} since - Optional timestamp (ms) to filter messages with timestamp > since
   * @returns {Promise<Array>} Array of messages (sorted newest-first from server, then sorted oldest-first)
   */
  async getMessages(roomId, limit = 50, since = null) {
    try {
      // Clamp limit to 1-200 as per contract
      const clampedLimit = Math.max(1, Math.min(200, limit))

      const params = { roomId, limit: clampedLimit }
      if (since !== null && since !== undefined) {
        params.since = since
      }

      const response = await apiClient.get('/chat/messages', { params })

      const messages = response.data || []
      // Server returns newest-first, but we sort to oldest-first for display
      messages.sort((a, b) => a.timestamp - b.timestamp)
      return messages
    } catch (error) {
      console.error('Failed to get messages:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Get all rooms for current user
   * @returns {Promise<Array>} Array of rooms
   */
  async getRooms() {
    try {
      const response = await apiClient.get('/chat/rooms')
      return response.data || []
    } catch (error) {
      console.error('Failed to get rooms:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Mark messages in a room as read
   * @param {string} roomId - Room ID
   * @param {string} messageId - Last message ID that was read
   * @param {number} timestamp - Timestamp of last message that was read
   * @returns {Promise<{success: boolean, lastReadTimestamp: number}>}
   */
  async markAsRead(roomId, messageId, timestamp) {
    try {
      const response = await apiClient.post(`/chat/rooms/${roomId}/mark-read`, {
        messageId,
        timestamp,
      })
      return response.data
    } catch (error) {
      console.error('Failed to mark room as read:', error)
      throw this.handleError(error)
    }
  }

  /**
   * Connect to WebSocket server
   * @param {string} token - JWT token for authentication
   * @returns {Promise<void>}
   */
  async connect(token) {
    console.log('connect')
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      // Already connected
      return
    }

    // Close existing connection if any
    this.disconnect()

    this.token = token
    this.isConnecting = true
    this.notifyConnectionState('connecting')

    return new Promise((resolve, reject) => {
      try {
        const wsUrl = Config.WEBSOCKET_URL
        const wsEndpoint = `${wsUrl}/ws/chat`
        this.ws = new WebSocket(wsEndpoint)

        this.ws.onopen = () => {
          console.log('WebSocket connected')
          this.isConnecting = false
          this.reconnectAttempts = 0
          this.reconnectDelay = 1000

          // Send authentication message
          this.authenticate(token)
        }

        this.ws.onmessage = (event) => {
          try {
            const message = JSON.parse(event.data)
            this.handleMessage(message)
          } catch (error) {
            console.error('Failed to parse WebSocket message:', error)
          }
        }

        this.ws.onerror = (error) => {
          console.error('WebSocket error:', error)
          this.isConnecting = false
          this.notifyConnectionState('error')
          reject(error)
        }

        this.ws.onclose = (event) => {
          console.log('WebSocket closed', event.code, event.reason)
          this.clearPingTimeout()
          this.isConnecting = false
          this.isAuthenticated = false

          // Attempt to reconnect if not a normal closure
          if (event.code !== 1000 && this.reconnectAttempts < this.maxReconnectAttempts) {
            this.scheduleReconnect()
          } else {
            this.notifyConnectionState('disconnected')
          }
        }

        // Resolve after connection is established (authentication will happen in onopen)
        setTimeout(() => {
          if (this.ws && this.ws.readyState === WebSocket.OPEN) {
            resolve()
          }
        }, 100)
      } catch (error) {
        this.isConnecting = false
        this.notifyConnectionState('error')
        reject(error)
      }
    })
  }

  /**
   * Authenticate with WebSocket server
   * @param {string} token - JWT token
   */
  authenticate(token) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(
        JSON.stringify({
          type: 'auth',
          token: token,
        }),
      )
    }
  }

  /**
   * Handle incoming WebSocket messages
   * @param {Object} message - Parsed message object
   */
  handleMessage(message) {
    switch (message.type) {
      case 'ready':
        this.isAuthenticated = true
        this.notifyConnectionState('connected')
        // Notify ready handler if registered (for resync trigger)
        if (this.readyHandler) {
          try {
            this.readyHandler()
          } catch (error) {
            console.error('Error in ready handler:', error)
          }
        }
        break
      case 'pong':
        // Clear any pending ping timeout when pong is received
        this.clearPingTimeout()
        break

      case 'pong':
        // Pong 响应由 ping() 方法处理，这里也传递给 messageHandler
        // 以防有其他地方需要监听 pong
        if (this.messageHandler) {
          try {
            this.messageHandler(message)
          } catch (error) {
            console.error('Error in message handler:', error)
          }
        }
        break

      case 'message':
      case 'read_receipt':
      case 'resync':
        // Call message handler if registered
        if (this.messageHandler) {
          try {
            this.messageHandler(message)
          } catch (error) {
            console.error('Error in message handler:', error)
          }
        }
        break

      case 'error':
        console.error('WebSocket error message:', message.message)
        // Call message handler for error so UI can show toast
        if (this.messageHandler) {
          try {
            this.messageHandler(message)
          } catch (error) {
            console.error('Error in message handler:', error)
          }
        }
        break

      default:
        console.log('Unknown message type:', message.type)
    }
  }

  /**
   * Send a chat message
   * @param {string} roomId - Room ID to send message to
   * @param {string} content - Message content
   */
  sendMessage(roomId, content) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected')
    }

    if (!this.isAuthenticated) {
      throw new Error('WebSocket is not authenticated')
    }

    if (!content || !content.trim()) {
      throw new Error('Message content cannot be empty')
    }

    this.ws.send(
      JSON.stringify({
        type: 'message',
        roomId: roomId,
        content: content.trim(),
      }),
    )
  }

  /**
   * Send a ping message to check if connection is alive
   * @returns {Promise<boolean>} true if pong received within timeout, false otherwise
   */
  async ping() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      return false
    }

    if (!this.isAuthenticated) {
      return false
    }

    return new Promise((resolve) => {
      // 设置超时（3 秒）
      const timeout = setTimeout(() => {
        resolve(false)
      }, 3000)

      // 临时保存原来的 message handler
      const originalHandler = this.messageHandler

      // 设置临时的 pong 处理器
      const pongHandler = (message) => {
        if (message.type === 'pong') {
          clearTimeout(timeout)
          // 恢复原来的 handler
          this.messageHandler = originalHandler
          resolve(true)
        } else if (originalHandler) {
          // 其他消息仍然传递给原来的 handler
          originalHandler(message)
        }
      }

      this.messageHandler = pongHandler

      try {
        // 发送 ping
        this.ws.send(JSON.stringify({ type: 'ping' }))
      } catch (error) {
        clearTimeout(timeout)
        this.messageHandler = originalHandler
        resolve(false)
      }
    })
  }

  /**
   * Resync messages for a room via WebSocket
   * @param {string} roomId - Room ID to resync
   * @param {number} since - Optional timestamp (ms) to fetch messages after this time
   * @param {number} limit - Number of messages to fetch (default: 50, clamped to 1-200)
   * @returns {Promise<void>}
   */
  resync(roomId, since = null, limit = 50) {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      throw new Error('WebSocket is not connected')
    }

    if (!this.isAuthenticated) {
      throw new Error('WebSocket is not authenticated')
    }

    // Clamp limit to 1-200 as per contract
    const clampedLimit = Math.max(1, Math.min(200, limit))

    const message = {
      type: 'resync',
      roomId: roomId,
      limit: clampedLimit,
    }

    if (since !== null && since !== undefined) {
      message.since = since
    }

    this.ws.send(JSON.stringify(message))
  }

  /**
   * Send a ping to verify the WebSocket connection health
   * If no pong is received within pingTimeoutMs, force a reconnect
   */
  sendPing() {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN || !this.isAuthenticated) {
      return false
    }

    // Clear any previous timer and start a new one
    this.clearPingTimeout()
    this.pingTimeoutId = setTimeout(() => {
      console.warn('WebSocket ping timeout, reconnecting...')
      this.disconnect()
      if (this.token) {
        this.connect(this.token).catch((error) => {
          console.error('Reconnect after ping timeout failed:', error)
        })
      }
    }, this.pingTimeoutMs)

    try {
      this.ws.send(
        JSON.stringify({
          type: 'ping',
        }),
      )
      return true
    } catch (error) {
      console.error('Failed to send ping:', error)
      this.clearPingTimeout()
      return false
    }
  }

  /**
   * Ensure WebSocket is connected/authenticated; if not, reconnect.
   * Useful when the app returns to the foreground.
   */
  ensureActiveConnection() {
    if (!this.token) return

    const state = this.getConnectionState()
    if (state === 'connected') {
      // Send a ping to verify health; reconnect on failure
      if (!this.sendPing()) {
        this.disconnect()
        this.connect(this.token).catch((error) => {
          console.error('Failed to reconnect after failed ping:', error)
        })
      }
      return
    }

    // For any non-connected state, attempt to reconnect
    this.connect(this.token).catch((error) => {
      console.error('Failed to ensure active connection:', error)
    })
  }

  /**
   * Disconnect from WebSocket server
   */
  disconnect() {
    this.clearPingTimeout()
    if (this.ws) {
      this.ws.close(1000, 'Client disconnect')
      this.ws = null
    }
    this.token = null
    this.isAuthenticated = false
    this.isConnecting = false
    this.reconnectAttempts = 0
    this.notifyConnectionState('disconnected')
  }

  clearPingTimeout() {
    if (this.pingTimeoutId) {
      clearTimeout(this.pingTimeoutId)
      this.pingTimeoutId = null
    }
  }

  /**
   * Schedule reconnection attempt with exponential backoff
   */
  scheduleReconnect() {
    this.reconnectAttempts++
    const delay = Math.min(this.reconnectDelay * Math.pow(2, this.reconnectAttempts - 1), 30000) // Max 30 seconds

    console.log(`Scheduling reconnect attempt ${this.reconnectAttempts} in ${delay}ms`)

    setTimeout(() => {
      if (this.token) {
        this.connect(this.token).catch((error) => {
          console.error('Reconnection failed:', error)
        })
      }
    }, delay)
  }

  /**
   * Register a message handler
   * @param {Function} handler - Function to call when message is received
   */
  onMessage(handler) {
    this.messageHandler = handler
  }

  /**
   * Register a connection state handler
   * @param {Function} handler - Function to call when connection state changes
   */
  onConnectionState(handler) {
    this.connectionStateHandler = handler
  }

  /**
   * Register a ready handler (called when WebSocket is authenticated and ready)
   * @param {Function} handler - Function to call when WebSocket is ready
   */
  onReady(handler) {
    this.readyHandler = handler
  }

  /**
   * Notify connection state handler
   * @param {string} state - Connection state: 'connecting', 'connected', 'disconnected', 'error'
   */
  notifyConnectionState(state) {
    if (this.connectionStateHandler) {
      try {
        this.connectionStateHandler(state)
      } catch (error) {
        console.error('Error in connection state handler:', error)
      }
    }
  }

  /**
   * Get current connection state
   * @returns {string} Connection state
   */
  getConnectionState() {
    if (!this.ws) return 'disconnected'
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING:
        return 'connecting'
      case WebSocket.OPEN:
        return this.isAuthenticated ? 'connected' : 'authenticating'
      case WebSocket.CLOSING:
        return 'disconnecting'
      case WebSocket.CLOSED:
        return 'disconnected'
      default:
        return 'disconnected'
    }
  }

  /**
   * Handle API errors
   * @private
   */
  handleError(error) {
    if (error.response) {
      const { status, data } = error.response
      switch (status) {
        case 400:
          return new Error(data.message || 'Invalid request, please check your input.')
        case 401:
          return new Error('Please login to continue.')
        case 403:
          return new Error('You do not have permission to perform this action.')
        case 404:
          return new Error('Resource not found.')
        case 503:
          return new Error(data.message || 'Service temporarily unavailable. Please try again later.')
        default:
          return new Error(data.message || 'An unexpected error occurred. Please try again later.')
      }
    }
    return new Error('Unable to connect to server. Please check your network connection.')
  }
}

const chatService = new ChatService()

// Export singleton instance
export default chatService
